import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as LucideIcons from "lucide-react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Save, Trash2, Plus, GripVertical, ArrowUp, ArrowDown } from "lucide-react";

function getIcon(name) {
  return LucideIcons[name] || LucideIcons.Sparkles;
}

const SUGGESTED_ICONS = [
  "Sparkles", "Tent", "BookOpen", "Flame", "Trophy", "Sprout", "Dog", "Baby",
  "Waves", "TreePine", "Apple", "Mountain", "Sun", "Moon", "Bird", "Fish",
  "Leaf", "Trees", "Heart", "Coffee", "Bike", "Music", "Camera", "Compass",
];

function AmenityRow({ amenity, onSave, onDelete, onMove }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(amenity);
  const Ico = getIcon(form.icon);

  useEffect(() => setForm(amenity), [amenity]);

  const save = async () => {
    await onSave(amenity.id, {
      icon: form.icon || "Sparkles",
      title: form.title,
      description: form.description,
    });
    setEditing(false);
  };

  if (!editing) {
    return (
      <div
        data-testid={`amenity-row-${amenity.id}`}
        className="p-4 flex items-start gap-3 border-b border-[var(--c-bio-green)]/10 hover:bg-[var(--c-bg-deep)]/40"
      >
        <div className="flex flex-col gap-0.5 pt-1 text-[var(--c-text)]/40">
          <button onClick={() => onMove(amenity.id, -1)} className="hover:text-[var(--c-firefly-gold)]" title="Subir">
            <ArrowUp className="w-3 h-3" />
          </button>
          <button onClick={() => onMove(amenity.id, 1)} className="hover:text-[var(--c-firefly-gold)]" title="Bajar">
            <ArrowDown className="w-3 h-3" />
          </button>
        </div>
        <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-[var(--c-bio-green)]/25 to-[var(--c-firefly-gold)]/25 flex items-center justify-center border border-[var(--c-bio-green)]/30">
          <Ico className="w-5 h-5 text-[var(--c-bio-green)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg text-[var(--c-text)] leading-tight">{amenity.title}</div>
          <div className="text-sm text-[var(--c-text)]/65 mt-0.5">{amenity.description}</div>
          <div className="text-[10px] text-[var(--c-text)]/40 mt-1 font-mono">icono: {amenity.icon}</div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => setEditing(true)}
            data-testid={`edit-amenity-${amenity.id}`}
            className="bg-transparent border border-[var(--c-bio-green)]/30 text-[var(--c-bio-green)] hover:bg-[var(--c-bio-green)] hover:text-[#0B1A0F]"
          >
            Editar
          </Button>
          <Button
            size="sm"
            onClick={() => onDelete(amenity.id)}
            data-testid={`delete-amenity-${amenity.id}`}
            className="bg-transparent border border-red-400/30 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 border-b border-[var(--c-bio-green)]/10 bg-[var(--c-firefly-gold)]/5">
      <div className="grid sm:grid-cols-12 gap-3">
        <div className="sm:col-span-3">
          <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Icono</label>
          <Input
            value={form.icon}
            onChange={(e) => setForm({ ...form, icon: e.target.value })}
            className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl font-mono text-sm"
            placeholder="Sparkles"
          />
          <div className="mt-2 flex items-center gap-2 text-[var(--c-bio-green)]">
            <Ico className="w-5 h-5" />
            <span className="text-xs text-[var(--c-text)]/55">preview</span>
          </div>
        </div>
        <div className="sm:col-span-4">
          <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Título</label>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl"
          />
        </div>
        <div className="sm:col-span-5">
          <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Descripción</label>
          <Textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl"
          />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
        <details className="text-xs text-[var(--c-text)]/60">
          <summary className="cursor-pointer hover:text-[var(--c-firefly-gold)]">Iconos sugeridos (click para usar)</summary>
          <div className="mt-2 flex flex-wrap gap-1.5 max-w-2xl">
            {SUGGESTED_ICONS.map((name) => {
              const I = getIcon(name);
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setForm({ ...form, icon: name })}
                  className={`px-2 py-1 rounded-md text-xs inline-flex items-center gap-1 border ${
                    form.icon === name
                      ? "bg-[var(--c-firefly-gold)] text-[#0B1A0F] border-[var(--c-firefly-gold)]"
                      : "bg-[var(--c-bg-deep)] text-[var(--c-text)]/70 border-[var(--c-bio-green)]/20 hover:border-[var(--c-firefly-gold)]/60"
                  }`}
                >
                  <I className="w-3 h-3" /> {name}
                </button>
              );
            })}
            <a
              href="https://lucide.dev/icons"
              target="_blank"
              rel="noreferrer"
              className="px-2 py-1 rounded-md text-xs text-[var(--c-firefly-gold)] hover:underline"
            >
              + ver todos los iconos
            </a>
          </div>
        </details>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => { setForm(amenity); setEditing(false); }}
            className="bg-transparent border border-[var(--c-bio-green)]/30 text-[var(--c-text)] hover:bg-[var(--c-bg-deep)]"
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={save}
            data-testid={`save-amenity-${amenity.id}`}
            className="bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] text-[#0B1A0F] font-semibold"
          >
            <Save className="w-3.5 h-3.5 mr-1" /> Guardar
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AmenityEditor() {
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const res = await api.get("/amenities");
      setItems(res.data || []);
    } catch {
      toast.error("No se pudo cargar amenidades");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (id, patch) => {
    try {
      await api.put(`/amenities/${id}`, patch);
      toast.success("Amenidad guardada");
      load();
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta amenidad?")) return;
    try {
      await api.delete(`/amenities/${id}`);
      toast.success("Amenidad eliminada");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const create = async () => {
    try {
      await api.post("/amenities", {
        icon: "Sparkles",
        title: "Nueva amenidad",
        description: "Describe esta amenidad",
        order: items.length,
      });
      toast.success("Amenidad creada");
      load();
    } catch {
      toast.error("No se pudo crear");
    }
  };

  const move = async (id, delta) => {
    const idx = items.findIndex((a) => a.id === id);
    if (idx < 0) return;
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= items.length) return;
    const reordered = [...items];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    // Persist new orders
    try {
      await Promise.all(
        reordered.map((a, i) => api.put(`/amenities/${a.id}`, { order: i }))
      );
      load();
    } catch {
      toast.error("No se pudo reordenar");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-3xl text-[var(--c-text)]">Amenidades</h2>
          <p className="text-sm text-[var(--c-text)]/70 mt-1">
            Agrega, edita, borra o reordena las amenidades de la ecocomunidad.
          </p>
        </div>
        <Button
          data-testid="amenity-create"
          onClick={create}
          className="bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] hover:opacity-90 text-[#0B1A0F] rounded-full font-semibold"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nueva amenidad
        </Button>
      </div>

      <div className="rounded-2xl border border-[var(--c-bio-green)]/15 bg-[var(--c-bg-forest)]/60 backdrop-blur overflow-hidden">
        {items.length === 0 && (
          <div className="p-10 text-center text-[var(--c-text)]/50">
            Sin amenidades. Crea la primera.
          </div>
        )}
        {items.map((a) => (
          <AmenityRow
            key={a.id}
            amenity={a}
            onSave={save}
            onDelete={remove}
            onMove={move}
          />
        ))}
      </div>
    </div>
  );
}
